import { useState, useEffect } from "react";
import { api, fmtDate } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { LiveMap } from "../../components/ops/LiveMap";
import { format, addDays } from "date-fns";
import { Button } from "../../components/ui/button";
import { ChevronLeft, ChevronRight, Users, Truck, MapPin, Map, LayoutList } from "lucide-react";

export default function Dispatch() {
  const [view, setView] = useState("map");
  const [mapData, setMapData] = useState(null);
  const [missions, setMissions] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [day, setDay] = useState(new Date());

  useEffect(() => {
    api.get("/missions").then((r) => setMissions(r.data)).catch(() => {});
    api.get("/resources/employees").then((r) => setEmployees(r.data)).catch(() => {});
    api.get("/resources/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    const poll = () => api.get("/ops/map").then((r) => setMapData(r.data)).catch(() => {});
    poll();
    const t = setInterval(poll, 5000);
    return () => clearInterval(t);
  }, []);

  const dayStr = format(day, "yyyy-MM-dd");
  const todays = missions
    .filter((m) => m.scheduled_date === dayStr)
    .sort((a, b) => (a.scheduled_time || "").localeCompare(b.scheduled_time || ""));
  const empName = (id) => employees.find((e) => e.id === id)?.name || "—";
  const vehName = (id) => vehicles.find((v) => v.id === id)?.name || "Unassigned";

  return (
    <div data-testid="ops-dispatch-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <p className="overline-cc">Field Operations</p>
          <h1 className="page-title mt-1.5">Dispatch</h1>
        </div>
        <div className="flex items-center gap-2 rounded-full border p-1">
          <button data-testid="dispatch-view-map" onClick={() => setView("map")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors duration-200 ${view === "map" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)]" : "text-muted-foreground hover:text-foreground"}`}>
            <Map className="w-3.5 h-3.5" /> Live Map
          </button>
          <button data-testid="dispatch-view-board" onClick={() => setView("board")}
            className={`px-4 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 transition-colors duration-200 ${view === "board" ? "bg-[var(--cc-accent)] text-[var(--cc-accent-fg)]" : "text-muted-foreground hover:text-foreground"}`}>
            <LayoutList className="w-3.5 h-3.5" /> Day Board
          </button>
        </div>
      </div>

      {view === "map" && (
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 rounded-xl border overflow-hidden h-[420px] lg:h-[560px] relative z-0">
            {mapData ? <LiveMap data={mapData} /> : (
              <div className="w-full h-full flex items-center justify-center text-sm text-muted-foreground">Loading live map…</div>
            )}
          </div>
          <div className="rounded-xl border bg-card flex flex-col max-h-[560px]" data-testid="dispatch-units-panel">
            <div className="px-5 py-3.5 border-b flex items-center justify-between">
              <h2 className="font-heading font-semibold text-sm">Active Units</h2>
              <span className="flex items-center gap-2 overline-cc"><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 pulse-dot" /> Live</span>
            </div>
            <div className="divide-y overflow-y-auto">
              {(mapData?.units || []).map((u) => (
                <div key={u.mission_id} className="px-5 py-4" data-testid={`unit-${u.mission_number}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-mono-cc text-xs text-[var(--cc-accent)]">{u.mission_number}</p>
                    <StatusBadge status={u.status} />
                  </div>
                  <p className="text-sm font-semibold mt-1.5">{u.customer} — {serviceName(u.service_type)}</p>
                  <div className="mt-2 space-y-1 text-xs text-muted-foreground">
                    <p className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5 text-amber-500" /> {u.vehicle}</p>
                    <p className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-emerald-500" /> {u.crew.length ? u.crew.map((c) => c.name).join(" + ") : "No crew assigned"}</p>
                    <p className="font-mono-cc">{fmtDate(u.date)} · {u.time}</p>
                  </div>
                </div>
              ))}
              {mapData && mapData.units.length === 0 && (
                <p className="px-5 py-8 text-xs text-muted-foreground text-center">No active missions on the map right now.</p>
              )}
            </div>
          </div>
        </div>
      )}

      {view === "board" && (
        <>
          <div className="flex items-center gap-3 mb-6">
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setDay(addDays(day, -1))} data-testid="dispatch-prev-day"><ChevronLeft className="w-4 h-4" /></Button>
            <p className="font-heading font-semibold w-36 text-center">{fmtDate(dayStr)}</p>
            <Button variant="outline" size="icon" className="rounded-full" onClick={() => setDay(addDays(day, 1))} data-testid="dispatch-next-day"><ChevronRight className="w-4 h-4" /></Button>
            <Button variant="ghost" size="sm" className="rounded-full" onClick={() => setDay(new Date())}>Today</Button>
          </div>
          <div className="grid lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 space-y-4">
              {todays.length === 0 && (
                <div className="rounded-xl border border-dashed p-12 text-center">
                  <p className="text-sm text-muted-foreground">No missions scheduled for this day.</p>
                </div>
              )}
              {todays.map((m) => (
                <div key={m.id} data-testid={`dispatch-mission-${m.mission_number}`} className="rounded-xl border bg-card overflow-hidden card-lift">
                  <div className="flex">
                    <div className="w-24 shrink-0 bg-secondary/60 flex flex-col items-center justify-center p-4 border-r">
                      <p className="font-mono-cc font-bold text-lg">{m.scheduled_time}</p>
                      <p className="font-mono-cc text-[10px] text-[var(--cc-accent)] mt-1">{m.mission_number}</p>
                    </div>
                    <div className="flex-1 p-5">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="font-heading font-semibold">{m.customer_name} — {serviceName(m.service_type)}</p>
                        <StatusBadge status={m.status} />
                      </div>
                      <div className="mt-3 grid sm:grid-cols-2 gap-2 text-xs text-muted-foreground">
                        <p className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-[var(--cc-accent)]" /> {m.pickup_address || "TBD"}</p>
                        <p className="flex items-center gap-1.5"><Truck className="w-3.5 h-3.5" /> {vehName(m.vehicle_id)}</p>
                        <p className="flex items-center gap-1.5 sm:col-span-2"><Users className="w-3.5 h-3.5" /> {m.crew.length ? m.crew.map(empName).join(", ") : "No crew assigned"}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="space-y-6">
              <div className="rounded-xl border bg-card">
                <div className="px-5 py-3.5 border-b"><h2 className="font-heading font-semibold text-sm">Crew Status</h2></div>
                <div className="divide-y max-h-72 overflow-y-auto">
                  {employees.map((e) => (
                    <div key={e.id} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{e.name}</p>
                        <p className="text-[11px] text-muted-foreground">{e.role}</p>
                      </div>
                      <StatusBadge status={e.status} />
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl border bg-card">
                <div className="px-5 py-3.5 border-b"><h2 className="font-heading font-semibold text-sm">Fleet Status</h2></div>
                <div className="divide-y">
                  {vehicles.map((v) => (
                    <div key={v.id} className="px-5 py-3 flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{v.name}</p>
                        <p className="text-[11px] text-muted-foreground font-mono-cc">{v.plate}</p>
                      </div>
                      <StatusBadge status={v.status} />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
